import {
	DeviceHealth,
	DeviceHealthCondition,
	HealthSeverity,
	MissingFeatureType,
	OrderedHealthConditionCollection
} from '@shure/cloud/shared/models/devices';

import {
	DeviceHealthFragment,
	HealthSeverity as SysApiHealthSeverity,
	InventoryDeviceFragment,
	PropertyPanelDeviceFragment
} from '../graphql/generated/cloud-sys-api';

type SysApiCondition = DeviceHealthFragment['conditions'][number];

const CONDITION_NAME_MAP = {
	DeviceBatteryLevelCondition: 'BatteryLevel',
	DeviceFirmwareIncompatibleCondition: 'FirmwareIncompatible',
	DeviceLicenseActivationFailedCondition: 'LicenseActivationFailed',
	DeviceLicenseOwnershipMismatchCondition: 'LicenseOwnershipMismatch',
	DeviceMissingCondition: 'Missing',
	DeviceOverTemperatureCondition: 'OverTemperature',
	DeviceRfInterferenceCondition: 'RfInterference'
} as const satisfies Record<SysApiCondition['__typename'], DeviceHealthCondition['name']>;

function getConditionSeverity(condition: SysApiCondition): HealthSeverity {
	switch (condition.__typename) {
		case 'DeviceOverTemperatureCondition':
			return HealthSeverity.overTemperature;
		case 'DeviceBatteryLevelCondition':
			return condition.severity === SysApiHealthSeverity.Critical
				? HealthSeverity.batteryLevelCritical
				: HealthSeverity.batteryLevelWarning;
		case 'DeviceLicenseActivationFailedCondition':
			return HealthSeverity.licenseActivationFailed;
		case 'DeviceRfInterferenceCondition':
			return HealthSeverity.rfInterference;
		case 'DeviceMissingCondition':
			return HealthSeverity.missing;
		case 'DeviceFirmwareIncompatibleCondition':
			return HealthSeverity.firmwareIncompatible;
		case 'DeviceLicenseOwnershipMismatchCondition':
			return HealthSeverity.licenseOwnershipMismatch;
	}
}

export function mapDeviceHealthConditionFromSysApi(value?: SysApiCondition): DeviceHealthCondition | undefined {
	if (!value) {
		return undefined;
	}

	if (value.__typename === 'DeviceBatteryLevelCondition') {
		return {
			name: CONDITION_NAME_MAP[value.__typename],
			detectedAt: value.detectedAt,
			severity: getConditionSeverity(value),
			detectedPercent: value.detectedPercent
		};
	}

	return {
		name: CONDITION_NAME_MAP[value.__typename],
		detectedAt: value.detectedAt,
		severity: getConditionSeverity(value)
	} satisfies DeviceHealthCondition;
}

export function deviceFragmentToHealthFeature(
	device: InventoryDeviceFragment | PropertyPanelDeviceFragment
): (DeviceHealth & MissingFeatureType) | undefined {
	if (!device.description.features.health?.supported || !device.features.health?.conditions) {
		return undefined;
	}

	const conditions = new OrderedHealthConditionCollection(
		device.features.health.conditions
			.map((fromSysApi) => mapDeviceHealthConditionFromSysApi(fromSysApi))
			.filter((value) => value !== undefined)
	);

	return {
		healthy: conditions.worstSeverity === undefined,
		conditions,
		isMissing: false
	};
}
